import type {ReactElement} from 'react';
import React, {useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {TextItemFillOptions} from '@Panels/poster-editor-text-fill-panel/components/text-item-fill-options';
import {TextItemColorPanel} from '@Panels/poster-editor-text-fill-panel/components/text-item-color-panel';
import {TextslideItemFillOptions} from '@Panels/poster-editor-text-fill-panel/components/textslide-item-fill-options';
import {TextslideItemColorPanel} from '@Panels/poster-editor-text-fill-panel/components/textslide-item-color-panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

interface PosterEditorTextFillPanelProps {
  panelId: string;
}

enum CurrentlyActiveFillPanel {
  DEFAULT = 'DEFAULT',
  COLOR_PANEL = 'COLOR_PANEL',
}

export default function PosterEditorTextFillPanel({...props}: PosterEditorTextFillPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const [currentlyActivePanel, setCurrentlyActivePanel] = useState(CurrentlyActiveFillPanel.DEFAULT);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  if (!activeItem) {
    return null;
  }

  if (!activeItem.isText() && !activeItem.isSlideshow()) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_fill')} />;
  };

  const getDefaultContent = (): ReactElement | null => {
    if (activeItem.isText()) {
      return (
        <TextItemFillOptions
          panelId={props.panelId}
          onOpenColorPanel={(index: number): void => {
            setSelectedColorIndex(index);
            setCurrentlyActivePanel(CurrentlyActiveFillPanel.COLOR_PANEL);
          }}
        />
      );
    }

    return (
      <TextslideItemFillOptions
        panelId={props.panelId}
        onOpenColorPanel={(index: number): void => {
          setSelectedColorIndex(index);
          setCurrentlyActivePanel(CurrentlyActiveFillPanel.COLOR_PANEL);
        }}
      />
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="text-fill-mobile-panel" hasMobileMorphingProperties hasMobileModalPadding>
        {getDefaultContent()}
      </ContextualMenu>
    );
  };

  const getColorPicker = (): ReactElement => {
    if (activeItem.isText()) {
      return (
        <TextItemColorPanel
          panelId={props.panelId}
          onClose={onClose}
          onBack={(): void => {
            setCurrentlyActivePanel(CurrentlyActiveFillPanel.DEFAULT);
          }}
          initialSelectedColorIndex={selectedColorIndex}
        />
      );
    }
    return (
      <TextslideItemColorPanel
        panelId={props.panelId}
        onClose={onClose}
        onBack={(): void => {
          setCurrentlyActivePanel(CurrentlyActiveFillPanel.DEFAULT);
        }}
        initialSelectedColorIndex={selectedColorIndex}
      />
    );
  };

  const getFillPanelContent = (): ReactElement => {
    switch (currentlyActivePanel) {
      case CurrentlyActiveFillPanel.COLOR_PANEL:
        return getColorPicker();
      case CurrentlyActiveFillPanel.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getFillPanelContent();
}
